import {
  Fragment,
  useState,
  useCallback,
  useMemo,
  type ComponentType,
  type FormEvent,
  type ReactNode,
} from 'react'

import PlainActionsButtons, {
  type PlainActionsButtonProps,
} from 'app/common/ui-next/plain-actions-button'

import PlainButton from 'app/common/ui-next/plain-button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from 'app/common/components/Modal'
import ControlsBar from 'app/common/ui-next/controls-bar'
// import PlainTooltip from 'app/common/ui-next/plain-tooltip'
import type { Dish } from 'app/dataTypes/dishes/dish'
import { useDishesAddToAllOrgs } from 'app/dataProviders/dishes/DishesData/addToAllOrgs'
import { useCheckedIds } from 'app/common/templates-next/table-template'

import { useDishesData } from '../../../dataProviders/dishes/DishesData'
import { dishesFeatures } from './_features'
import styles from './DishesActionsButton.module.scss'
import { EditTypeModal } from './EditTypeModal/EditTypeModal'

type DishesActionsButtonProps = Omit<PlainActionsButtonProps, 'children'> & {
  children: ReactNode
}

const DishesPlainActionsButtons = PlainActionsButtons as ComponentType<DishesActionsButtonProps>

/**
 *  Кнопка с выпадающим меню с действиями над выделенным
 */

const DishesActionsButton = () => {
  const [actionInProgress, setActionInProgress] = useState<'copy' | 'delete' | null>(null)

  const selection = useCheckedIds()

  const [{ dishes, nomenclatureActive }, { delete: deleteDishes, copy: copyDishes }] =
    useDishesData()

  const itemsSelected = useMemo<Array<Dish>>(() => {
    const selectionMap = new Set(selection)
    return dishes.filter(({ id }) => selectionMap.has(String(id)))
  }, [dishes, selection])

  const selectedIds = useMemo(() => itemsSelected.map(({ id }) => id), [itemsSelected])
  const selectedIdsAsNumbers = useMemo(
    () => selectedIds.map((id) => parseInt(id, 10)),
    [selectedIds],
  )
  const selectedNumber = itemsSelected.length

  // modals
  const [showDeleteModal, setDeleteModalVisible] = useState(false)
  const toggleDeleteModal = useCallback(() => setDeleteModalVisible((state) => !state), [])
  const [showCopyModal, setCopyModalVisible] = useState(false)
  const toggleCopyModal = useCallback(() => setCopyModalVisible((state) => !state), [])

  const [showEditTypeModal, setEditTypeModalVisible] = useState(false)
  const toggleEditTypeModal = useCallback(() => setEditTypeModalVisible((state) => !state), [])

  const { submit: addToAllOrgs, pending } = useDishesAddToAllOrgs({ dishIds: selectedIds })

  const handleAddToAllOrgs = useCallback(async () => {
    await addToAllOrgs()
  }, [addToAllOrgs])

  // delete items
  const handleDishesRemoveItems = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setActionInProgress('delete')
      const res = await deleteDishes(selectedIds)
      setActionInProgress(null)

      if (res) {
        setDeleteModalVisible(false)
      }
    },
    [selectedIds, deleteDishes],
  )

  // copy items
  const handleDishesCopyItems = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setActionInProgress('copy')
      const res = await copyDishes(selectedIdsAsNumbers)
      setActionInProgress(null)

      if (res) {
        setCopyModalVisible(false)
      }
    },
    [selectedIdsAsNumbers, copyDishes],
  )

  return (
    <Fragment>
      {/* <PlainTooltip
        text={!selectedNumber ? 'Необходимо выбрать хотя бы одну продукцию' : null}
        enableOverflow
      > */}
      <DishesPlainActionsButtons
        collapsed
        value="Изменить"
        number={selectedNumber}
        valueMask="Изменить (%n%)"
        disabled={!selectedNumber || pending}
        className={styles.actionsButton}
      >
        <PlainButton
          id="add_to_all_schools"
          onClick={handleAddToAllOrgs}
          disabled={!selectedNumber || !!actionInProgress}
        >
          {selectedNumber > 0
            ? `Добавить во все заведения (${selectedNumber})`
            : 'Добавить во все заведения'}
        </PlainButton>

        {dishesFeatures.selectedDishesTypesChange && (
          <PlainButton
            id="dishtype"
            onClick={toggleEditTypeModal}
            disabled={!selectedNumber || !!actionInProgress}
          >
            {selectedNumber > 0
              ? `Редактировать тип продукции (${selectedNumber})`
              : 'Редактировать тип продукции'}
          </PlainButton>
        )}

        {!nomenclatureActive && ( // нельзя копировать, если используется номенклатура
          <PlainButton
            id="copy"
            onClick={toggleCopyModal}
            disabled={!selectedNumber || !!actionInProgress}
          >
            {selectedNumber > 0 ? `Копировать (${selectedNumber})` : 'Копировать'}
          </PlainButton>
        )}

        <PlainButton
          id="delete"
          onClick={toggleDeleteModal}
          disabled={!selectedNumber || !!actionInProgress}
        >
          {selectedNumber > 0 ? `Удалить (${selectedNumber})` : 'Удалить'}
        </PlainButton>
      </DishesPlainActionsButtons>
      {/* </PlainTooltip> */}

      {showDeleteModal && (
        <Modal toggleModal={toggleDeleteModal}>
          <form onSubmit={handleDishesRemoveItems}>
            <ModalHeader>{'Удаление продукции'}</ModalHeader>
            <ModalContent maxHeight={300}>
              <ul>
                {itemsSelected.map(({ id, title }) => (
                  <li key={id}>{title}</li>
                ))}
              </ul>
            </ModalContent>
            <ModalFooter>
              <ControlsBar middle>
                <PlainButton type="submit" disabled={!selectedNumber || !!actionInProgress} danger>
                  {actionInProgress === 'delete' ? 'Удаляется...' : 'Удалить'}
                </PlainButton>
                <PlainButton outline onClick={toggleDeleteModal}>
                  {'Отмена'}
                </PlainButton>
              </ControlsBar>
            </ModalFooter>
          </form>
        </Modal>
      )}

      {showCopyModal && (
        <Modal toggleModal={toggleCopyModal}>
          <form onSubmit={handleDishesCopyItems}>
            <ModalHeader>{'Копирование продукции'}</ModalHeader>
            <ModalContent maxHeight={300}>
              <ul>
                {itemsSelected.map(({ id, title }) => (
                  <li key={id}>{title}</li>
                ))}
              </ul>
            </ModalContent>
            <ModalFooter>
              <ControlsBar middle>
                <PlainButton type="submit" disabled={!selectedNumber || !!actionInProgress}>
                  {actionInProgress === 'copy' ? 'Копируется...' : 'Копировать'}
                </PlainButton>
                <PlainButton outline onClick={toggleCopyModal}>
                  {'Отмена'}
                </PlainButton>
              </ControlsBar>
            </ModalFooter>
          </form>
        </Modal>
      )}

      {showEditTypeModal && dishesFeatures.selectedDishesTypesChange && (
        <EditTypeModal itemsSelected={itemsSelected} onClose={toggleEditTypeModal} />
      )}
    </Fragment>
  )
}

export default DishesActionsButton
